import { Box, Stack } from '@mui/material';

import type { RoadmapNode } from "../../api/roadmap-service";
import type { StationProgress } from "../../utils/roadmap-progress";

export type StationState = 'finished' | 'progress' | 'todo' | 'planned' | 'empty';

type StationLook = { status: string; statusColor: string; bar: string; background: string; border: string; line: string };

const LOOKS: Record<StationState, Omit<StationLook, 'status'>> = {
    finished: { statusColor: '#08660D', bar: '#229A16', background: '#F1FCEA', border: '#229A16', line: 'solid' },
    progress: { statusColor: '#1A55B3', bar: '#2065D1', background: '#FFFFFF', border: '#2065D1', line: 'solid' },
    todo: { statusColor: '#4F5B67', bar: '#CFD6DE', background: '#FFFFFF', border: '#CFD6DE', line: 'solid' },
    planned: { statusColor: '#4F5B67', bar: '#CFD6DE', background: '#F4F6F8', border: '#919EAB', line: 'dashed' },
    empty: { statusColor: '#4F5B67', bar: '#CFD6DE', background: '#FFFFFF', border: '#CFD6DE', line: 'solid' },
};

export function stationState(node: RoadmapNode, progress: StationProgress): StationState {
    if (node.planned) return 'planned';
    if (!progress.lessons) return 'empty';
    if (progress.finished === progress.lessons) return 'finished';

    return progress.verified ? 'progress' : 'todo';
}

function stationStatus(state: StationState, progress: StationProgress): string {
    switch (state) {
        case 'finished': return 'Finished';
        case 'progress': return `${ Math.round(progress.verified / progress.lessons * 100) }%`;
        case 'todo': return 'Not started';
        case 'planned': return 'Planned';
        case 'empty': return 'No lessons yet';
    }
}

export type RoadmapStationProps = {
    node: RoadmapNode;
    progress: StationProgress;
    /** The cover of the course the station opens, when the catalogue has one. */
    cover?: string;
    /** Set when the station lies above the selected belt; it fades out and says where it starts. */
    fromLevel?: string;
    onOpen: () => void;
};

/** One part of a lane: a course or topic of the role, with how far the user got with its lessons. */
export function RoadmapStation({ node, progress, cover, fromLevel, onOpen }: RoadmapStationProps) {
    const state = stationState(node, progress);
    const look = LOOKS[state];
    const status = fromLevel ? `From ${ fromLevel }` : stationStatus(state, progress);
    const share = state === 'finished' ? 100 : progress.lessons ? progress.verified / progress.lessons * 100 : 0;

    return (
        <Box component="button" type="button" onClick={ onOpen } aria-label={ `${ node.title } — ${ status }` }
             data-testid="roadmap-station" sx={ {
            position: 'relative', width: '100%', textAlign: 'left', font: 'inherit', color: '#161C24', cursor: 'pointer',
            bgcolor: look.background, border: `2px ${ look.line } ${ look.border }`, borderRadius: '14px', p: 1.25,
            display: 'flex', flexDirection: 'column', gap: 1, opacity: fromLevel ? 0.4 : 1,
            '&:hover': { opacity: 1, boxShadow: '0 4px 12px rgba(22,28,36,0.08)' },
            '&:focus-visible': { outline: '2px solid #2065D1', outlineOffset: 2 },
        } }>
            <Stack direction="row" spacing={ 1.25 } alignItems="center">
                { cover ? (
                    <Box component="img" src={ cover } alt=""
                         sx={ { width: 56, height: 42, objectFit: 'cover', borderRadius: '8px', flexShrink: 0 } }/>
                ) : (
                    <Box aria-hidden="true" sx={ { width: 56, height: 42, flexShrink: 0, borderRadius: '8px', bgcolor: '#EDF0F3', color: '#637381',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' } }>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 19.5V5a2 2 0 0 1 2-2h14v16H6a2 2 0 0 0-2 2z"/>
                        </svg>
                    </Box>
                ) }
                <Box component="span" sx={ { fontSize: 14, lineHeight: '18px', fontWeight: 700 } }>{ node.title }</Box>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={ 1 } sx={ { width: '100%' } }>
                <Box aria-hidden="true" sx={ { flexGrow: 1, height: 5, borderRadius: '3px', bgcolor: '#EDF0F3', overflow: 'hidden' } }>
                    <Box sx={ { height: 5, width: `${ share }%`, bgcolor: look.bar } }/>
                </Box>
                <Box component="span" sx={ { fontSize: 11, fontWeight: 800, color: fromLevel ? '#4F5B67' : look.statusColor, whiteSpace: 'nowrap' } }>
                    { status }
                </Box>
            </Stack>
        </Box>
    );
}
